import React, { useState, useEffect } from "react";

/**
 * Toast notification component for displaying quote alerts
 */
export function QuoteNotification({ quote, onDismiss, onOpenChat }) {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(onDismiss, 300); // Allow fade-out animation
    }, 5000); // Auto-dismiss after 5 seconds

    return () => clearTimeout(timer);
  }, [onDismiss]);

  const handleDismiss = (e) => {
    // Prevent the click from bubbling up to the open-chat handler
    e?.stopPropagation?.();
    setIsVisible(false);
    setTimeout(onDismiss, 300);
  };

  const handleOpenChat = (e) => {
    // Prevent the dismiss button from bubbling
    e?.stopPropagation?.();
    if (onOpenChat) onOpenChat(quote);
    // Dismiss after opening chat
    setIsVisible(false);
    setTimeout(onDismiss, 300);
  };

  return (
    <div
      onClick={handleOpenChat}
      className={`fixed top-4 right-4 z-50 cursor-pointer transition-all duration-300 ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2"
      }`}
    >
      <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-4 min-w-[320px] max-w-md">
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
              <svg
                className="w-5 h-5 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900">
                {quote.type === "counter" ? "Counter Offer" : "New Quote"}
              </h4>
              <p className="text-sm text-gray-600">{quote.dealerName}</p>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            title="Dismiss"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
        <div className="mt-2">
          <p className="text-2xl font-bold text-blue-600">
            ${quote.price.toLocaleString()}
          </p>
          {quote.pricePerUnit && (
            <p className="text-sm text-gray-500">
              ${quote.pricePerUnit.toFixed(2)} per {quote.unit}
            </p>
          )}
          {quote.notes && (
            <p className="mt-2 text-sm text-gray-700 italic">"{quote.notes}"</p>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Container for managing multiple notifications
 */
export function NotificationContainer({ children }) {
  return (
    <div className="fixed top-0 right-0 z-50 pointer-events-none">
      <div className="pointer-events-auto space-y-2 p-4">{children}</div>
    </div>
  );
}

/**
 * Hook to manage toast notifications
 */
export function useNotifications() {
  const [notifications, setNotifications] = useState([]);

  const addNotification = (notification) => {
    const id = Date.now();
    setNotifications((prev) => [...prev, { ...notification, id }]);
  };

  const removeNotification = (id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  return {
    notifications,
    addNotification,
    removeNotification,
  };
}
